import type { ListFilterOperator } from '@zag/core/common';
import type { BadgeColor } from '../../badge/badge';
import type { BulkSelectionPayload } from '../../bulk-edit/bulk-edit.models';
import type { CollectionSelectionState } from '../../selection/collection-selection.service';

/**
 * View mode option for collection layout
 */
export interface CollectionViewMode {
  /** Unique identifier for this view mode */
  id: string;
  /** Translation key for the label */
  label: string;
  /** Material icon name */
  icon: string;
  /** Whether this mode displays items in a card grid (true) or list (false) */
  isCardMode: boolean;
}

/**
 * A generic filter option for the collection layout filter list
 */
export interface CollectionFilterOption<T = string> {
  /** The value emitted when this option is selected */
  value: T;
  /** Translation key for the label */
  label: string;
  /** Optional icon name */
  icon?: string;
}

export type CollectionFilterPrimitive = string | number | boolean;

/**
 * Filter group shown in the collection layout sidebar.
 */
export interface CollectionFilterGroup<T = CollectionFilterPrimitive> {
  /** Stable group identifier */
  id: string;
  /** API-facing filter field; defaults to the group id. */
  filterField?: string;
  /** API filter operator; defaults to `eq` or `in` from group cardinality. */
  filterOperator?: ListFilterOperator;
  /** Group label or translation key */
  label: string;
  /** Available options inside the group */
  options: CollectionFilterOption<T>[];
  /** Whether the group supports selecting multiple options */
  allowMultiple?: boolean;
  /** Optional custom clear label or translation key */
  clearLabel?: string;
}

/**
 * Current selected filter values keyed by group id.
 */
export type CollectionFilterValues = Record<string, CollectionFilterPrimitive[]>;

/**
 * A small status badge rendered in the collection profile card header.
 *
 * Use it to signal the scope of the displayed collection, e.g. a "Personal"
 * marker when a list only contains the current user's own records.
 */
export interface CollectionProfileBadge {
  /** Translation key (or literal text) for the badge label */
  label: string;
  /** Optional Material icon name shown before the label */
  icon?: string;
  /** Colour variant (defaults to 'info') */
  color?: BadgeColor;
}

/**
 * Domain KPI rendered in the sidebar stats card next to Total and Filtered.
 */
export interface CollectionStat {
  /** Stable identifier used for tracking */
  id: string;
  /** Material icon name */
  icon: string;
  /** Translation key for the label */
  label: string;
  /** Pre-formatted value */
  value: string | number;
}

/**
 * Base sort option interface (for display purposes only, no compareFn)
 */
export interface CollectionSortOptionBase {
  /** Unique identifier for this sort option */
  id: string;
  /** Translation key for the label */
  label: string;
  /** Optional icon name */
  icon?: string;
}

/**
 * Sort option for collection layout with client-side comparison function
 */
export interface CollectionSortOption<T = unknown> extends CollectionSortOptionBase {
  /** Comparison function for client-side sorting */
  compareFn?: (a: T, b: T) => number;
}

/**
 * Configuration for the collection layout component.
 *
 * Note: Search is now handled globally via TopBar and SearchService.
 * The searchPlaceholder option has been removed.
 */
export interface CollectionLayoutConfig<T = unknown> {
  /**
   * Catalog title shown in the profile card at the top of the sidebar.
   * e.g. 'People', 'Teams'
   */
  title?: string;
  /**
   * Material icon name shown in the profile card.
   * e.g. 'people', 'groups'
   */
  icon?: string;
  /**
   * Short description shown below the title in the profile card.
   */
  description?: string;
  /**
   * Optional badge shown in the profile card header to flag the collection's
   * scope (e.g. a "Personal" marker when only the user's own records are shown).
   */
  badge?: CollectionProfileBadge;
  /** Available view modes (card/list toggle options) */
  viewModes?: CollectionViewMode[];
  /** Available sort options */
  sortOptions?: CollectionSortOption<T>[];
  /** Available grouped filter options */
  filterGroups?: CollectionFilterGroup[];
  /**
   * Translation key for the filter card label (e.g. 'common.filter').
   * When set together with filterOptions, a filter list card is shown.
   */
  filterLabel?: string;
  /** Available filter options shown as a toggleable list card */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterOptions?: CollectionFilterOption<any>[];
  /** Prefix for localStorage keys (e.g., 'persons' -> 'persons-view-mode') */
  storageKeyPrefix?: string;
  /** Default view mode ID (defaults to first mode or 'card') */
  defaultViewMode?: string;
  /** Default sort option ID */
  defaultSortBy?: string;
  /** Default sort direction (defaults to 'asc') */
  defaultSortDirection?: CollectionSortDirection;
  /** Number of items to show initially (default: 20) */
  initialBatchSize?: number;
  /** Number of items to load on scroll (default: 10) */
  loadMoreBatchSize?: number;
  /** Pixels from bottom to trigger load more (default: 200) */
  scrollThreshold?: number;
}

/**
 * Sort direction for collection layout
 */
export type CollectionSortDirection = 'asc' | 'desc';

/**
 * Persisted state for collection layout
 */
export interface CollectionLayoutState {
  /** Current view mode ID */
  viewMode: string;
  /** Current sort option ID */
  sortBy?: string;
  /** Current sort direction */
  sortDirection?: CollectionSortDirection;
  /** Current grouped filter selections */
  filterValues?: CollectionFilterValues;
}

export interface CollectionSelectionContext {
  selection: CollectionSelectionState;
  selectedCount: number;
  matchingTotal: number;
  allMatching: boolean;
  selectionPayload: BulkSelectionPayload;
}
